// FIRST, before any module that reads the address: takes a Vipps return's
// parameters out of it and puts the view back (#508, `lib/paymentReturn.ts`).
import './paymentReturnBoot';
import './fonts.css';
import './index.css';
import { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { LocaleProvider } from './i18n';
// The app component has no mounting code of its own — we mount its default export here.
import Padel24App from './app/Padel24App';
import { BanepadDeviceRoute } from './components/banepad/BanepadDeviceRoute';
import { SuperadminRoute } from './components/platform';
import { PasskeyProbe } from './components/diagnostics/PasskeyProbe';
import { currentSurface, subscribeSurface, type Surface } from './app/surface';
import { StepUpPrompt } from './components/auth/StepUpPrompt';

// THE BOOT WATCHDOG IS OFF ONCE WE ARE HERE (#579). `index.html` arms a timer
// that replaces the page with «kunne ikke laste appen» if no module ever runs —
// the shape of a 404 on the bundle, which is otherwise indistinguishable from
// an app that is merely thinking. Reaching this line is proof it loaded.
//
// Cleared HERE rather than after `createRoot`, because a render that throws is
// still a page that loaded: the watchdog's job is «did the module arrive», and
// React's own error is a better report of anything after that.
declare global {
  interface Window {
    __starpointBoot?: ReturnType<typeof setTimeout>;
  }
}
if (typeof window !== 'undefined' && window.__starpointBoot !== undefined) {
  clearTimeout(window.__starpointBoot);
  delete window.__starpointBoot;
}

const el = document.getElementById('root');
if (!el) throw new Error('Fant ikke #root i index.html');

// WHICH SURFACE IS STATE NOW, not a decision taken once at module load
// (RBAC-3 follow-up). The paths and what they mean are unchanged:
//
//   /pad  physical Banepad pads, authenticating as a DEVICE via a stored
//         device token rather than a user login — mounted before the
//         Padel24App auth gate + StoreProvider, court resolved from the token
//         and never from the URL;
//   /sa   the Starpoint-INTERNAL control panel, whose staff sign in with Entra
//         — a different identity model from the club portal's, so it is not
//         behind the club login gate;
//   /diagnostics/passkeys
//         the WebAuthn probe (#676) — a measurement page with no login, no
//         store and no server call, which the `passkey-probe` app build opens;
//   else  the club portal.
//
// What changed is that moving between them no longer reloads the page, so an
// in-memory organization context survives the trip. See `app/surface.ts` for
// why that matters and why the storage rule did not move to accommodate it.
function AppSurface() {
  const [surface, setSurface] = useState<Surface>(currentSurface);
  useEffect(() => subscribeSurface(setSurface), []);

  if (surface === 'device') return <BanepadDeviceRoute />;
  if (surface === 'superadmin') return <SuperadminRoute />;
  if (surface === 'diagnostics') return <PasskeyProbe />;
  return <Padel24App />;
}

// ── «PROVE YOU ARE STILL THERE», ONCE, ABOVE EVERY SURFACE (#750) ──
//
// `reauthenticationSignal` holds ONE handler, so there must be exactly one
// prompt mounted — two would mean the second silently replaced the first and
// whichever surface mounted last would be the only one that could ask.
//
// Here rather than inside `Padel24App` for that reason: that component has a
// dozen early returns, so a mount inside it would be a mount in one branch,
// and the stale answer can arrive on any of them. It renders nothing until a
// 403 says otherwise.
//
// NOT ON THE PAD. A pad authenticates as a DEVICE: it holds no account
// session, nothing it can reach is gated on freshness, and it has no person in
// front of it to prove anything — a dialog there could only ever be an
// unanswerable one. `device` therefore returns before this is mounted.
function AppWithStepUp() {
  const [surface, setSurface] = useState<Surface>(currentSurface);
  useEffect(() => subscribeSurface(setSurface), []);
  if (surface === 'device') return <BanepadDeviceRoute />;
  // The probe holds no session either, so there is nobody to step up.
  if (surface === 'diagnostics') return <PasskeyProbe />;
  return (
    <>
      <AppSurface />
      <StepUpPrompt />
    </>
  );
}

// NB: deliberately WITHOUT <React.StrictMode> for now. StrictMode double-runs
// effects in dev, and the blanket-persist effects (HANDOVER §13.20) are vulnerable to
// that. Turn it on later once the loaded flags are verified. No effect on prod.
createRoot(el).render(
  // ONE PROVIDER AT THE ROOT (#310a), above every route — the pad and the
  // superadmin shell included. A surface that is not translated yet simply
  // does not call `useT`; one that is cannot be mounted somewhere the
  // provider is missing, which is the failure `useLocale` throws for.
  <LocaleProvider>
    <AppWithStepUp />
  </LocaleProvider>,
);
